import { useCallback, useEffect } from "react";

import { useAgentExecutionClient } from "src/api/agents/execution";

const PREWARM_COOLDOWN_MS = 20_000; // dedup window in ms

// Tracks in-flight ping requests to prevent duplicates
const pingPromiseMap = new Map<string, Promise<void>>();

// Per-key cooldown timestamps
const lastPingTimestampMap = new Map<string, number>();

type UseCopilotPrewarmParams = {
  agentId: string | undefined;
  sessionId: string | null;
  enabled: boolean;
};

/**
 * Pre-warms an AgentCore container for copilot invocations.
 * Sends a lightweight ping so the first actual invocation has lower latency.
 *
 * - Deduplicates via in-flight tracking and a 20 s cooldown window
 * - Fire-and-forget: errors are silently swallowed
 * - Auto-triggers on mount when `enabled` is true
 */
export const useCopilotPrewarm = ({
  agentId,
  sessionId,
  enabled,
}: UseCopilotPrewarmParams): void => {
  const { client } = useAgentExecutionClient();

  const ping = useCallback(() => {
    if (!client || !agentId || !sessionId) return;

    const key = agentId;

    // Skip if ping already in-flight
    if (pingPromiseMap.has(key)) return;

    // Dedupe: skip if pinged within cooldown window
    const lastPing = lastPingTimestampMap.get(key) ?? 0;
    if (Date.now() - lastPing < PREWARM_COOLDOWN_MS) return;

    lastPingTimestampMap.set(key, Date.now());

    const pingPromise = client
      .pingCopilotsManagementApiV0PingPost({
        pingRequest: {
          agent_id: agentId,
          session_id: sessionId,
        },
      })
      .catch(() => {}) // Swallow errors
      .finally(() => {
        if (pingPromiseMap.get(key) !== pingPromise) {
          // Outdated/stale promise, don't clean up the newer one
          return;
        }
        pingPromiseMap.delete(key);
      }) as unknown as Promise<void>;

    pingPromiseMap.set(key, pingPromise);
  }, [client, agentId, sessionId]);

  useEffect(() => {
    if (enabled) {
      ping();
    }
  }, [enabled, ping]);
};

/** Reset module-level state for testing purposes. */
export const __resetCopilotPrewarmState = (): void => {
  pingPromiseMap.clear();
  lastPingTimestampMap.clear();
};
